import React, { useEffect, useState } from "react";

import {
  Switch,
  FormControlLabel,
  useMediaQuery,
  Card,
} from "@material-ui/core";

import {
  BulkActionsToolbar,
  BulkDeleteButton,
  DateField,
  DateInput,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { Pagination } from "../../../components/Pagination";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { LogStatus } from "../../../utils/constants";
import { SearchField } from "../../../components/common/SearchField";
import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { APP_VERSION } from "../../../utils/constants";
import { ClipboardTextField } from "../../../components/common/CopyToClipboardButton";
import { TenantRender } from "./TenantField";

const LogSearchField = ({ label, alwaysOn }) => {
  const listContext = useListContext();
  const { displayedFilters, filterValues, setFilters } = listContext;

  const handleSearch = (value) => {
    if (value) {
      delete filterValues.or;
      setFilters(
        {
          ...filterValues,
          or: [
            { user: { matchPhrasePrefix: value } },
            { user: { wildcard: `*${value}*` } },
            { url: { matchPhrasePrefix: value } },
            { url: { wildcard: `*${value}*` } },
            { appVersion: { matchPhrasePrefix: value } },
            { appVersion: { wildcard: `*${value}*` } },
            { message: { matchPhrasePrefix: value } },
            { message: { wildcard: `*${value}*` } },
            { exception: { matchPhrasePrefix: value } },
            { exception: { wildcard: `*${value}*` } },
          ],
        },
        displayedFilters
      );
    } else {
      delete filterValues.or;
      setFilters(filterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="logSearchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const AppVersionFilter = ({ alwaysOn }) => {
  const { displayedFilters, filterValues, setFilters } = useListContext();
  const [isCurrentVersion, setIsCurrentVersion] = useState(false);

  useEffect(() => {
    if (filterValues?.appVersion?.eq === APP_VERSION) {
      setIsCurrentVersion(true);
    } else if (filterValues?.appVersion?.ne === APP_VERSION) {
      setIsCurrentVersion(false);
    }
  }, [filterValues]);

  const handleRefreshChange = (event) => {
    const refreshEnabled = event.target.checked;
    setIsCurrentVersion(refreshEnabled);

    if (refreshEnabled) {
      setFilters(
        { ...filterValues, appVersion: { eq: APP_VERSION } },
        displayedFilters
      );
    } else {
      setFilters(
        { ...filterValues, appVersion: { ne: APP_VERSION } },
        displayedFilters
      );
    }
  };

  return (
    <FormControlLabel
      label={APP_VERSION}
      style={{ marginLeft: "0px", marginTop: "0px" }}
      control={
        <Switch
          name="refresh"
          size="small"
          checked={isCurrentVersion}
          onChange={handleRefreshChange}
        />
      }
      alwaysOn={alwaysOn}
    />
  );
};

const getListFilters = (isMedium) => [
  <LogSearchField label="Search Logs" alwaysOn />,
  <DateInput
    source="createdAt.lte"
    label="Created before..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
  <DateInput
    source="createdAt.gte"
    label="Created after..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
];

const ListActions = ({ isXSmall }) => (
  <TopToolbar>
    <AppVersionFilter alwaysOn />
    {!isXSmall && <FilterButton />}
    <RefreshButton />
  </TopToolbar>
);

const BulkListActions = ({ onUnselectItems, ...props }) => (
  <BulkDeleteButton onClick={() => onUnselectItems()} onDelete {...props} />
);

export const LogList = (props) => {
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const [defaultFilter] = useState({ status: LogStatus.NEW });
  const [defaultSearchFilter] = useState({
    status: { eq: LogStatus.NEW },
  });

  const defaultSort = {
    field: `{"key":"logByDateByTenant", "search":"createdAt"}`,
    order: "DESC",
  };

  const sessionPerPage = Number(
    localStorage.getItem(`${props.resource}_perPage`)
  );
  const [defaultPerPage] = useState(sessionPerPage || 10);

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Error Logs",
    defaultFilter,
    defaultSearchFilter,
    defaultPerPage,
    defaultSort,
    resource: "logs",
  });

  const getSortBy = (resource) => {
    return `{"key":"logByDateByTenant", "search":"${resource}"}`;
  };

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext?.perPage]);

  return (
    <ListContextProvider value={listContext}>
      <BulkActionsToolbar>
        <BulkListActions {...listContext} />
      </BulkActionsToolbar>
      <Title title={<ForceTitle title="Error Logs" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(mdUp)}
        actions={<ListActions isXSmall={xsDown} />}
      />
      <Card>
        {xsDown ? (
          <CustomizableDatagrid
            rowClick="show"
            storageProp="LogList_xsmall"
            defaultColumns={["createdAt", "Tenant"]}
          >
            <DateField
              source="createdAt"
              label="Created At"
              showTime
              sortable={false}
            />
            <FunctionField
              source="tenantId"
              label="Tenant"
              render={TenantRender}
            />
            <EditButton />
          </CustomizableDatagrid>
        ) : mdDown ? (
          <CustomizableDatagrid
            rowClick="show"
            storageProp="LogList_small"
            defaultColumns={["createdAt", "message", "appVersion"]}
          >
            <DateField
              source="createdAt"
              label="Created At"
              showTime
              sortable={true}
              sortBy={getSortBy("createdAt")}
            />
            <TextField source="appType" label="App Type" sortable={true} />
            <FunctionField
              source="tenantId"
              label="Tenant"
              render={TenantRender}
            />
            <ClipboardTextField on source="user" label="User" />
            <ClipboardTextField on source="message" label="Message" />
            <TextField
              source="appVersion"
              label="App Version"
              sortable={true}
            />
            <TextField source="url" label="URL" />
            <EditButton />
          </CustomizableDatagrid>
        ) : (
          <CustomizableDatagrid
            rowClick="show"
            hasBulkActions={true}
            storageProp="LogList_large"
            defaultColumns={[
              "createdAt",
              "appType",
              "tenant",
              "user",
              "message",
              "appVersion",
            ]}
          >
            <DateField
              source="createdAt"
              label="Created At"
              showTime
              sortable={true}
              sortBy={getSortBy("createdAt")}
            />
            <TextField source="appType" label="App Type" sortable={true} />
            <FunctionField
              source="tenantId"
              label="Tenant"
              render={TenantRender}
            />
            <ClipboardTextField on source="user" label="User" />
            <ClipboardTextField source="message" label="Message" />
            <TextField
              source="appVersion"
              label="App Version"
              sortable={true}
            />
            <TextField source="url" label="URL" />
            <EditButton />
          </CustomizableDatagrid>
        )}

        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};
